import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, CalendarCheck, MapPin, Clock, Users, Check, X, UserSwitch, Camera, CheckCircle,
  UserPlus, Eye, Phone, Briefcase, Tag, ThumbsUp, ThumbsDown, UsersThree, CaretRight, Calendar
} from "@phosphor-icons/react";
import QRScanner from "@/components/QRScanner";
import InviteLinkModal from "@/components/InviteLinkModal";

export default function MeetingsPage() {
  const { token, user, isAdmin } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [checkinResult, setCheckinResult] = useState(null);
  const [scanStep, setScanStep] = useState("select"); // "select" or "scan"

  // Visitor states
  const [visitorDialogOpen, setVisitorDialogOpen] = useState(false);
  const [visitorMeeting, setVisitorMeeting] = useState(null);
  const [visitorForm, setVisitorForm] = useState({ name: "", contact: "", category: "", business_name: "", interested_to_join: false, invited_by_id: "" });
  const [viewVisitorsOpen, setViewVisitorsOpen] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [chapterMembers, setChapterMembers] = useState([]);
  const [chapterCategories, setChapterCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedRSVP, setExpandedRSVP] = useState({});
  const [rsvpData, setRsvpData] = useState({});
  const [inviteModal, setInviteModal] = useState({ open: false, category: "", chapterName: "", chapterId: "", meetingId: "" });

  const isPresident = user?.role === "president";
  const canCreateMeeting = isAdmin || isPresident;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    chapter_id: "",
    meeting_date: "",
    meeting_time: "",
    location: "",
    full_address: "",
    google_maps_link: ""
  });

  const canAddVisitors = ["super_admin", "president", "vice_president", "secretary"].includes(user?.role);
  const [myVisitorCount, setMyVisitorCount] = useState({ total: 0, present: 0 });
  const [myVisitorsList, setMyVisitorsList] = useState([]);
  const [myPersonalVisitors, setMyPersonalVisitors] = useState([]);
  const [showMyVisitors, setShowMyVisitors] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const isLeadership = ["super_admin", "president", "vice_president", "secretary"].includes(user?.role);
      const requests = [
        axios.get(`${API}/meetings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/chapters`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my-visitors-count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/my-visitors`, { headers: { Authorization: `Bearer ${token}` } })
      ];
      if (isLeadership) {
        requests.push(
          axios.get(`${API}/my-visitors?mine_only=true`, { headers: { Authorization: `Bearer ${token}` } })
        );
      }
      const results = await Promise.all(requests);
      setMeetings(results[0].data);
      setChapters(results[1].data);
      setMyVisitorCount(results[2].data);
      setMyVisitorsList(results[3].data);
      if (isLeadership) {
        setMyPersonalVisitors(results[4].data);
      }
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    } finally {
      setLoading(false);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  usePolling(fetchData, 10000);

  const toggleCategories = async (meetingId, chapterId) => {
    if (expandedCategories[meetingId]) {
      setExpandedCategories(prev => ({ ...prev, [meetingId]: false }));
      return;
    }
    if (!chapterCategories[chapterId]) {
      try {
        const res = await axios.get(`${API}/categories/availability?chapter_id=${chapterId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setChapterCategories(prev => ({ ...prev, [chapterId]: res.data }));
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    }
    setExpandedCategories(prev => ({ ...prev, [meetingId]: true }));
  };

  const toggleRSVPList = async (meetingId) => {
    if (expandedRSVP[meetingId]) {
      setExpandedRSVP(prev => ({ ...prev, [meetingId]: false }));
      return;
    }
    if (!rsvpData[meetingId]) {
      try {
        const res = await axios.get(`${API}/meetings/${meetingId}/rsvp`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRsvpData(prev => ({ ...prev, [meetingId]: res.data }));
      } catch (err) {
        console.error("Failed to fetch RSVPs:", err);
      }
    }
    setExpandedRSVP(prev => ({ ...prev, [meetingId]: true }));
  };

  const handleCreateMeeting = async () => {
    const chapterId = isPresident ? user.chapter_id : formData.chapter_id;
    if (!formData.title || !chapterId || !formData.meeting_date || !formData.meeting_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const payload = {
        ...formData,
        chapter_id: chapterId,
        location: [formData.location, formData.full_address].filter(Boolean).join(" | ")
      };
      if (formData.google_maps_link) {
        payload.location += ` | ${formData.google_maps_link}`;
      }
      await axios.post(`${API}/meetings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Meeting created successfully!");
      setDialogOpen(false);
      setFormData({ title: "", description: "", chapter_id: "", meeting_date: "", meeting_time: "", location: "", full_address: "", google_maps_link: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    }
  };

  // Substitute states
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subMeetingId, setSubMeetingId] = useState(null);
  const [subForm, setSubForm] = useState({ name: "", phone: "" });

  const handleRSVP = async (meetingId, response) => {
    if (response === "substitute") {
      setSubMeetingId(meetingId);
      setSubForm({ name: "", phone: "" });
      setSubDialogOpen(true);
      return;
    }
    try {
      await axios.post(`${API}/meetings/${meetingId}/rsvp`, {
        meeting_id: meetingId,
        response
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`RSVP: ${response.replace("_", " ")}`);
    } catch (error) {
      toast.error("Failed to submit RSVP");
    }
  };

  const handleSubstituteSubmit = async () => {
    if (!subForm.name.trim()) { toast.error("Substitute name is required"); return; }
    if (!subForm.phone || subForm.phone.replace(/\D/g, "").length !== 10) { toast.error("Enter a valid 10-digit phone number"); return; }
    try {
      await axios.post(`${API}/meetings/${subMeetingId}/rsvp`, {
        meeting_id: subMeetingId,
        response: "substitute",
        substitute_name: subForm.name,
        substitute_phone: subForm.phone
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Substitute details submitted!");
      setSubDialogOpen(false);
    } catch (error) {
      toast.error("Failed to submit substitute details");
    }
  };

  const handleQRScan = async (decodedText) => {
    if (!selectedMeeting) return;
    try {
      const response = await axios.post(`${API}/attendance/scan-id`, {
        meeting_id: selectedMeeting.id,
        qr_data: decodedText
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.already_checked_in) {
        setCheckinResult({ success: true, message: response.data.message });
      } else {
        setCheckinResult({ success: true, message: response.data.message });
      }
      toast.success(response.data.message);
    } catch (err) {
      setCheckinResult({ success: false, message: err.response?.data?.detail || "Scan failed. Invalid ID card." });
      toast.error(err.response?.data?.detail || "Scan failed");
    }
  };

  const openScanner = () => {
    setCheckinResult(null);
    setSelectedMeeting(null);
    setScanStep("select");
    setScanDialogOpen(true);
  };

  const openAddVisitor = async (meeting) => {
    setVisitorMeeting(meeting);
    setVisitorForm({ name: "", contact: "", category: "", business_name: "", interested_to_join: false, invited_by_id: "" });
    // Fetch chapter members for "Invited By" dropdown
    try {
      const res = await axios.get(`${API}/members?chapter_id=${meeting.chapter_id || ""}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapterMembers(res.data || []);
    } catch (err) {
      setChapterMembers([]);
    }
    setVisitorDialogOpen(true);
  };

  const handleAddVisitor = async () => {
    if (!visitorForm.name || !visitorForm.contact || !visitorForm.business_name) {
      toast.error("Please fill in name, contact, and business name");
      return;
    }
    try {
      await axios.post(`${API}/meetings/${visitorMeeting.id}/visitors`, visitorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Visitor added successfully!");
      setVisitorDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add visitor");
    }
  };

  const openViewVisitors = async (meeting) => {
    setVisitorMeeting(meeting);
    try {
      const res = await axios.get(`${API}/meetings/${meeting.id}/visitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisitors(res.data);
    } catch (err) {
      setVisitors([]);
    }
    setViewVisitorsOpen(true);
  };

  const handleMarkPresent = async (meetingId, visitorId) => {
    try {
      const res = await axios.patch(`${API}/meetings/${meetingId}/visitors/${visitorId}/mark-present`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update view visitors dialog
      setVisitors(prev => prev.map(v => v.id === visitorId ? { ...v, status: res.data.status } : v));
      // Update my visitors list
      setMyVisitorsList(prev => prev.map(group => ({
        ...group,
        visitors: group.visitors.map(v => v.id === visitorId ? { ...v, status: res.data.status } : v),
        present: group.visitors.reduce((acc, v) => acc + (v.id === visitorId ? (res.data.status === "present" ? 1 : 0) : (v.status === "present" ? 1 : 0)), 0)
      })));
      // Update counts
      setMyVisitorCount(prev => ({
        total: prev.total,
        present: res.data.status === "present" ? prev.present + 1 : prev.present - 1
      }));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update visitor status");
    }
  };

  const [activeTab, setActiveTab] = useState("meetings");

  return (
    <>
      <SEO title="Meetings" description="View upcoming chapter meetings, RSVP, and track your attendance record." path="/meetings" noIndex />
      <div className="space-y-6 animate-fadeInUp">
        {/* Page Header with Stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-primary">Meetings</h1>
          <div className="flex gap-2 flex-wrap">
            {(isAdmin || isPresident || user?.role === "vice_president" || user?.role === "secretary") && (
            <Button
              onClick={openScanner}
              className="bg-bch-gold hover:bg-bch-gold-dark text-black"
              data-testid="scan-qr-checkin-btn"
            >
              <Camera className="w-5 h-5 mr-2" weight="duotone" /> Scan ID
            </Button>
            )}
            {canCreateMeeting && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-bch-teal hover:bg-bch-teal-dark" data-testid="create-meeting-btn">
                  <Plus className="w-5 h-5 mr-2" /> Schedule Meeting
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-[var(--border-color)] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-primary text-xl">Schedule New Meeting</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-secondary">Meeting Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="Weekly Chapter Meeting"
                    />
                  </div>
                  {!isPresident ? (
                    <div className="space-y-2">
                      <Label className="text-secondary">Chapter *</Label>
                      <Select value={formData.chapter_id} onValueChange={(v) => setFormData(p => ({ ...p, chapter_id: v }))}>
                        <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary">
                          <SelectValue placeholder="Select chapter" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-[var(--border-color)]">
                          {chapters.map((ch) => (
                            <SelectItem key={ch.id} value={ch.id} className="text-primary">{ch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-secondary">Chapter</Label>
                      <div className="px-4 py-2.5 bg-surface border border-[var(--border-color)] rounded-md text-primary text-sm">
                        {chapters.find(ch => ch.id === user.chapter_id)?.name || user.chapter_name || "Your Chapter"}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-secondary">Date *</Label>
                      <Input
                        type="date"
                        value={formData.meeting_date}
                        onChange={(e) => setFormData(p => ({ ...p, meeting_date: e.target.value }))}
                        className="bg-surface border-[var(--border-color)] text-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Time *</Label>
                      <Input
                        type="time"
                        value={formData.meeting_time}
                        onChange={(e) => setFormData(p => ({ ...p, meeting_time: e.target.value }))}
                        className="bg-surface border-[var(--border-color)] text-primary"
                        data-testid="meeting-time-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Venue Name</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="Hotel Taj, Conference Hall B"
                      data-testid="meeting-venue-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Full Address</Label>
                    <Input
                      value={formData.full_address}
                      onChange={(e) => setFormData(p => ({ ...p, full_address: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="123 Main Street, Mumbai, Maharashtra 400001"
                      data-testid="meeting-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-red-500" weight="fill" /> Google Maps Link
                    </Label>
                    <Input
                      value={formData.google_maps_link}
                      onChange={(e) => setFormData(p => ({ ...p, google_maps_link: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="https://maps.google.com/..."
                      data-testid="meeting-maps-link-input"
                    />
                    <p className="text-muted text-xs">Paste a Google Maps link so members can find the venue easily</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="Meeting agenda..."
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleCreateMeeting} className="bg-bch-gold hover:bg-bch-gold-dark text-black font-medium">
                    Create Meeting
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div className="bg-card border border-[var(--border-color)] rounded-xl p-3 flex items-center gap-2" data-testid="visitors-invited-card">
            <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
              <UserPlus className="w-4 h-4 text-violet-500" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-muted text-[9px] sm:text-[10px] uppercase tracking-wider font-medium truncate">{canAddVisitors ? "Invited" : "My Invites"}</p>
              <p className="text-primary text-base sm:text-lg font-bold">{myVisitorCount.total}</p>
            </div>
          </div>
          <div className="bg-card border border-[var(--border-color)] rounded-xl p-3 flex items-center gap-2" data-testid="visitors-success-card">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
              <CheckCircle className="w-4 h-4 text-emerald-500" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-muted text-[9px] sm:text-[10px] uppercase tracking-wider font-medium truncate">Visitors Present</p>
              <p className="text-primary text-base sm:text-lg font-bold">{myVisitorCount.present}</p>
            </div>
          </div>
          <div className="bg-card border border-[var(--border-color)] rounded-xl p-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-500/15 flex items-center justify-center shrink-0">
              <CalendarCheck className="w-4 h-4 text-sky-500" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-muted text-[9px] sm:text-[10px] uppercase tracking-wider font-medium truncate">Meetings</p>
              <p className="text-primary text-base sm:text-lg font-bold">{meetings.length}</p>
            </div>
          </div>
          <div className="bg-card border border-[var(--border-color)] rounded-xl p-3 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
              <UsersThree className="w-4 h-4 text-amber-500" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-muted text-[9px] sm:text-[10px] uppercase tracking-wider font-medium truncate">Success Rate</p>
              <p className="text-primary text-base sm:text-lg font-bold">{myVisitorCount.total > 0 ? Math.round((myVisitorCount.present / myVisitorCount.total) * 100) : 0}%</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-surface border border-[var(--border-color)] rounded-xl p-1" data-testid="meetings-tabs">
          <button
            onClick={() => setActiveTab("meetings")}
            className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              activeTab === "meetings"
                ? "bg-bch-teal text-white shadow-sm"
                : "text-muted hover:text-primary hover:bg-card"
            }`}
            data-testid="tab-meetings"
          >
            <CalendarCheck className="w-4 h-4" weight={activeTab === "meetings" ? "fill" : "duotone"} />
            Meetings
          </button>
          <button
            onClick={() => { setActiveTab("visitors"); if (!showMyVisitors) setShowMyVisitors(true); }}
            className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              activeTab === "visitors"
                ? "bg-violet-500 text-white shadow-sm"
                : "text-muted hover:text-primary hover:bg-card"
            }`}
            data-testid="tab-visitors"
          >
            <UsersThree className="w-4 h-4" weight={activeTab === "visitors" ? "fill" : "duotone"} />
            {user?.role === "super_admin" ? "All Visitors" : canAddVisitors ? "Chapter Visitors" : "My Visitors"}
          </button>
          {canAddVisitors && (
          <button
            onClick={() => setActiveTab("my-invited")}
            className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-2 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              activeTab === "my-invited"
                ? "bg-pink-500 text-white shadow-sm"
                : "text-muted hover:text-primary hover:bg-card"
            }`}
            data-testid="tab-my-invited"
          >
            <UserPlus className="w-4 h-4" weight={activeTab === "my-invited" ? "fill" : "duotone"} />
            Invited by Me
          </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === "visitors" ? (
          <div className="space-y-4" data-testid="my-visitors-section">
            {myVisitorsList.length === 0 ? (
              <div className="text-center py-16 bg-card border border-[var(--border-color)] rounded-2xl">
                <UsersThree className="w-12 h-12 text-muted/30 mx-auto mb-3" />
                <p className="text-secondary font-medium">No visitors yet</p>
                <p className="text-muted text-xs mt-1">Invite visitors via category links from meetings</p>
              </div>
            ) : (
              myVisitorsList.map((meetingGroup) => (
                <div key={meetingGroup.meeting_id} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid={`visitor-group-${meetingGroup.meeting_id}`}>
                  <div className="flex items-center justify-between p-4 bg-surface/50 border-b border-[var(--border-color)]">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-bch-teal/15 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-bch-teal" weight="duotone" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-primary">{meetingGroup.meeting_title}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          {meetingGroup.chapter_name && (
                            <span className="text-[10px] text-bch-teal font-medium">{meetingGroup.chapter_name}</span>
                          )}
                          {meetingGroup.meeting_date && (
                            <span className="text-[10px] text-muted">{new Date(meetingGroup.meeting_date).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">
                      {meetingGroup.present}/{meetingGroup.total} present
                    </span>
                  </div>
                  <div className="divide-y divide-[var(--border-color)]">
                    {meetingGroup.visitors.map((v) => (
                      <div key={v.id} className="flex items-center justify-between px-4 py-3" data-testid={`my-visitor-${v.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-primary truncate">{v.name}</p>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                              v.status === "present" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                            }`}>
                              {v.status === "present" ? "Present" : "Registered"}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[11px] text-muted flex items-center gap-1"><Phone className="w-3 h-3" />{v.contact}</span>
                            <span className="text-[11px] text-muted flex items-center gap-1"><Briefcase className="w-3 h-3" />{v.business_name}</span>
                            {v.category && <span className="text-[11px] text-muted flex items-center gap-1"><Tag className="w-3 h-3" />{v.category}</span>}
                          </div>
                          {v.invited_by_name && (
                            <p className="text-[10px] text-pink-500 mt-0.5 flex items-center gap-1"><UserPlus className="w-3 h-3" />Invited by {v.invited_by_name}</p>
                          )}
                        </div>
                        {canAddVisitors && (
                          <button
                            onClick={() => handleMarkPresent(meetingGroup.meeting_id, v.id)}
                            data-testid={`my-mark-present-${v.id}`}
                            className={`text-[11px] font-medium px-3 py-1.5 rounded-lg transition-colors shrink-0 ml-3 ${
                              v.status === "present"
                                ? "bg-red-100 text-red-600 hover:bg-red-200"
                                : "bg-bch-teal/15 text-bch-teal hover:bg-bch-teal/25"
                            }`}
                          >
                            {v.status === "present" ? "Undo" : "Mark Present"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === "my-invited" && canAddVisitors ? (
          <div className="space-y-4" data-testid="my-invited-section">
            {myPersonalVisitors.length === 0 ? (
              <div className="text-center py-16 bg-card border border-[var(--border-color)] rounded-2xl">
                <UserPlus className="w-12 h-12 text-muted/30 mx-auto mb-3" />
                <p className="text-secondary font-medium">No visitors invited by you yet</p>
                <p className="text-muted text-xs mt-1">Share invite links from meeting categories to invite visitors</p>
              </div>
            ) : (
              myPersonalVisitors.map((meetingGroup) => (
                <div key={meetingGroup.meeting_id} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden" data-testid={`my-invited-group-${meetingGroup.meeting_id}`}>
                  <div className="flex items-center justify-between p-4 bg-pink-50/50 border-b border-[var(--border-color)]">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-pink-500/15 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-pink-500" weight="duotone" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-primary">{meetingGroup.meeting_title}</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          {meetingGroup.chapter_name && (
                            <span className="text-[10px] text-pink-500 font-medium">{meetingGroup.chapter_name}</span>
                          )}
                          {meetingGroup.meeting_date && (
                            <span className="text-[10px] text-muted">{new Date(meetingGroup.meeting_date).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="text-[11px] bg-pink-100 text-pink-700 px-2.5 py-1 rounded-full font-semibold">
                      {meetingGroup.present}/{meetingGroup.total} present
                    </span>
                  </div>
                  <div className="divide-y divide-[var(--border-color)]">
                    {meetingGroup.visitors.map((v) => (
                      <div key={v.id} className="flex items-center justify-between px-4 py-3" data-testid={`invited-visitor-${v.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-primary truncate">{v.name}</p>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                              v.status === "present" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                            }`}>
                              {v.status === "present" ? "Present" : "Registered"}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-[11px] text-muted flex items-center gap-1"><Phone className="w-3 h-3" />{v.contact}</span>
                            <span className="text-[11px] text-muted flex items-center gap-1"><Briefcase className="w-3 h-3" />{v.business_name}</span>
                            {v.category && <span className="text-[11px] text-muted flex items-center gap-1"><Tag className="w-3 h-3" />{v.category}</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => handleMarkPresent(meetingGroup.meeting_id, v.id)}
                          data-testid={`invited-mark-present-${v.id}`}
                          className={`text-[11px] font-medium px-3 py-1.5 rounded-lg transition-colors shrink-0 ml-3 ${
                            v.status === "present"
                              ? "bg-red-100 text-red-600 hover:bg-red-200"
                              : "bg-bch-teal/15 text-bch-teal hover:bg-bch-teal/25"
                          }`}
                        >
                          {v.status === "present" ? "Undo" : "Mark Present"}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
        <>
        {/* Meeting List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : meetings.length === 0 ? (
          <div className="text-center py-20 bg-card border border-[var(--border-color)] rounded-2xl">
            <CalendarCheck className="w-16 h-16 text-muted/30 mx-auto mb-4" weight="duotone" />
            <p className="text-secondary">No meetings scheduled</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {meetings.map((meeting) => (
              <div key={meeting.id} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-sky-300/40">
                {/* 1. Meeting Details */}
                <div className="p-4 sm:p-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <span className={`px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider ${
                        meeting.status === "upcoming" ? "bg-emerald-100 text-emerald-700" : "bg-surface text-secondary"
                      }`}>{meeting.status}</span>
                      <h3 className="text-primary text-lg sm:text-xl font-bold mt-2 leading-tight break-words">{meeting.title}</h3>
                      <p className="text-bch-gold text-sm sm:text-base font-medium mt-0.5">{meeting.chapter_name}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-base">
                    <div className="flex items-center gap-2.5 text-secondary">
                      <div className="w-8 h-8 rounded-md bg-sky-500/15 flex items-center justify-center shrink-0">
                        <CalendarCheck className="w-4 h-4 text-sky-500" weight="fill" />
                      </div>
                      <span className="text-sm font-medium">{meeting.meeting_date}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-secondary">
                      <div className="w-8 h-8 rounded-md bg-amber-500/15 flex items-center justify-center shrink-0">
                        <Clock className="w-4 h-4 text-amber-500" weight="fill" />
                      </div>
                      <span className="text-sm font-medium">{meeting.meeting_time}</span>
                    </div>
                  </div>
                  {meeting.location && (() => {
                    const parts = meeting.location.split(" | ");
                    const venue = parts[0];
                    const address = parts[1] && !parts[1].startsWith("http") ? parts[1] : null;
                    const mapsLink = parts.find(p => p.startsWith("http"));
                    return (
                      <div className="mt-3">
                        <div className="flex items-start gap-2.5 text-secondary">
                          <div className="w-8 h-8 rounded-md bg-rose-500/15 flex items-center justify-center shrink-0 mt-0.5">
                            <MapPin className="w-4 h-4 text-rose-500" weight="fill" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-medium">{venue}</span>
                            {address && <p className="text-muted text-sm mt-0.5">{address}</p>}
                            {mapsLink && (
                              <a href={mapsLink} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-500 text-sm font-medium hover:text-blue-600 mt-1"
                                data-testid={`maps-link-${meeting.id}`}>
                                <MapPin className="w-3.5 h-3.5" weight="fill" /> Google Maps
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {meeting.description && <p className="text-muted text-sm mt-3">{meeting.description}</p>}
                </div>

                {/* 2. RSVP */}
                {meeting.status === "upcoming" && (
                  <div className="px-4 sm:px-5 py-3 border-t border-[var(--border-color)] bg-surface/30">
                    <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-2">RSVP</p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => handleRSVP(meeting.id, "attending")}
                        className="bg-bch-teal/20 text-bch-teal hover:bg-bch-teal/30 text-xs sm:text-sm h-8 sm:h-9">
                        <Check className="w-4 h-4 mr-1" /> Attending
                      </Button>
                      <Button size="sm" onClick={() => handleRSVP(meeting.id, "not_attending")}
                        variant="ghost" className="text-red-400 hover:bg-red-500/10 text-xs sm:text-sm h-8 sm:h-9">
                        <X className="w-4 h-4 mr-1" /> Can't Attend
                      </Button>
                      <Button size="sm" onClick={() => handleRSVP(meeting.id, "substitute")}
                        variant="ghost" className="text-bch-gold hover:bg-bch-gold/10 text-xs sm:text-sm h-8 sm:h-9">
                        <UserSwitch className="w-4 h-4 mr-1" /> Substitute
                      </Button>
                    </div>
                  </div>
                )}

                {/* 3. View Attendance */}
                {canAddVisitors && (
                  <div className="px-5 py-3 border-t border-[var(--border-color)]">
                    <button onClick={() => toggleRSVPList(meeting.id)}
                      className="flex items-center justify-between text-base text-sky-500 hover:text-sky-600 font-medium transition-colors w-full"
                      data-testid={`toggle-rsvp-${meeting.id}`}>
                      <span className="flex items-center gap-2">
                        <Users className="w-5 h-5" weight="duotone" />
                        {expandedRSVP[meeting.id] ? "Hide Attendance" : "View Attendance"}
                      </span>
                      <CaretRight className={`w-5 h-5 transition-transform duration-200 ${expandedRSVP[meeting.id] ? "rotate-90" : ""}`} />
                    </button>
                    {expandedRSVP[meeting.id] && rsvpData[meeting.id] && (() => {
                      const rsvps = rsvpData[meeting.id];
                      const attending = rsvps.filter(r => r.status === "attending");
                      const notAttending = rsvps.filter(r => r.status === "not_attending");
                      const subs = rsvps.filter(r => r.status === "substitute");
                      return (
                        <div className="mt-3 space-y-3" data-testid={`rsvp-panel-${meeting.id}`}>
                          {attending.length > 0 && (
                            <div>
                              <p className="text-xs uppercase tracking-wider font-semibold mb-2 text-emerald-500">Attending ({attending.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {attending.map(r => (
                                  <span key={r.user_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
                                    <Check className="w-3.5 h-3.5" weight="bold" /> {r.user_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {notAttending.length > 0 && (
                            <div>
                              <p className="text-xs uppercase tracking-wider font-semibold mb-2 text-red-400">Not Attending ({notAttending.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {notAttending.map(r => (
                                  <span key={r.user_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm">
                                    <X className="w-3.5 h-3.5" weight="bold" /> {r.user_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {subs.length > 0 && (
                            <div>
                              <p className="text-xs uppercase tracking-wider font-semibold mb-2 text-amber-500">Substitute ({subs.length})</p>
                              <div className="space-y-2">
                                {subs.map(r => (
                                  <div key={r.user_id} className="flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg text-sm">
                                    <UserSwitch className="w-3.5 h-3.5 shrink-0" weight="bold" />
                                    <span className="font-medium">{r.user_name}</span>
                                    {r.substitute_name && (
                                      <span className="text-amber-600/70">&rarr; {r.substitute_name}
                                        {r.substitute_phone && <span className="ml-1 font-mono">({r.substitute_phone})</span>}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {rsvps.length === 0 && <p className="text-muted text-sm py-2 text-center">No RSVPs yet</p>}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* 4. Invite Visitors (Categories) */}
                <div className="px-5 py-3 border-t border-[var(--border-color)]">
                  <button onClick={() => toggleCategories(meeting.id, meeting.chapter_id)}
                    className="flex items-center justify-between text-base text-violet-500 hover:text-violet-600 font-medium transition-colors w-full"
                    data-testid={`toggle-categories-${meeting.id}`}>
                    <span className="flex items-center gap-2">
                      <Tag className="w-5 h-5" weight="duotone" />
                      {expandedCategories[meeting.id] ? "Hide Categories" : "Invite Visitors"}
                    </span>
                    <CaretRight className={`w-5 h-5 transition-transform duration-200 ${expandedCategories[meeting.id] ? "rotate-90" : ""}`} />
                  </button>
                  {expandedCategories[meeting.id] && chapterCategories[meeting.chapter_id] && (
                    <div className="mt-3 space-y-3" data-testid={`categories-panel-${meeting.id}`}>
                      <div className="flex items-center gap-2 text-sm text-muted">
                        <span>{chapterCategories[meeting.chapter_id].taken_count} taken</span>
                        <span>&middot;</span>
                        <span className="text-bch-teal font-medium">{chapterCategories[meeting.chapter_id].available_count} available</span>
                      </div>
                      {chapterCategories[meeting.chapter_id].taken.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-2">Taken — Click to invite!</p>
                          <div className="flex flex-wrap gap-2">
                            {chapterCategories[meeting.chapter_id].taken.map(t => (
                              <button key={t.category}
                                onClick={() => setInviteModal({ open: true, category: t.category, chapterName: chapterCategories[meeting.chapter_id].chapter_name, chapterId: meeting.chapter_id, meetingId: meeting.id })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-all cursor-pointer"
                                title={`${t.member_name} — Click to invite`}
                                data-testid={`meeting-taken-invite-${t.category.replace(/\s+/g, "-").toLowerCase()}`}>
                                <CheckCircle className="w-3.5 h-3.5" weight="fill" /> {t.category}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {chapterCategories[meeting.chapter_id].available.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-2">Available — Click to invite!</p>
                          <div className="flex flex-wrap gap-2">
                            {chapterCategories[meeting.chapter_id].available.map(cat => (
                              <button key={cat}
                                onClick={() => setInviteModal({ open: true, category: cat, chapterName: chapterCategories[meeting.chapter_id].chapter_name, chapterId: meeting.chapter_id, meetingId: meeting.id })}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-bch-teal/10 text-bch-teal border border-bch-teal/20 rounded-lg text-sm font-medium hover:bg-bch-teal/20 transition-all cursor-pointer"
                                data-testid={`meeting-invite-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                                <Plus className="w-3.5 h-3.5" weight="bold" /> {cat}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 5. Add Visitor & View Visitors */}
                {canAddVisitors && (
                  <div className="px-5 py-3 border-t border-[var(--border-color)] bg-surface/20 flex items-center gap-3">
                    <Button size="sm" onClick={() => openAddVisitor(meeting)}
                      className="bg-violet-500/15 text-violet-600 hover:bg-violet-500/25 text-sm h-9"
                      data-testid={`add-visitor-${meeting.id}`}>
                      <UserPlus className="w-4 h-4 mr-1.5" weight="bold" /> Add Visitor
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openViewVisitors(meeting)}
                      className="text-secondary hover:text-primary text-sm h-9"
                      data-testid={`view-visitors-${meeting.id}`}>
                      <Eye className="w-4 h-4 mr-1.5" /> View Visitors
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {/* Substitute Details Dialog */}
        <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
          <DialogContent className="bg-card border-[var(--border-color)] max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-primary text-lg flex items-center gap-2">
                <UserSwitch className="w-5 h-5 text-amber-500" weight="fill" />
                Substitute Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-muted text-xs">Please provide your substitute's details so they can attend on your behalf.</p>
              <div className="space-y-2">
                <Label className="text-secondary">Full Name *</Label>
                <Input
                  value={subForm.name}
                  onChange={(e) => setSubForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-surface border-[var(--border-color)] text-primary"
                  placeholder="Substitute's full name"
                  data-testid="sub-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-secondary">Mobile Number *</Label>
                <Input
                  value={subForm.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setSubForm(p => ({ ...p, phone: val }));
                  }}
                  maxLength={10}
                  className="bg-surface border-[var(--border-color)] text-primary font-mono"
                  placeholder="9876543210"
                  data-testid="sub-phone-input"
                />
                {subForm.phone && subForm.phone.length !== 10 && (
                  <p className="text-red-400 text-xs">Must be exactly 10 digits</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSubstituteSubmit} className="bg-amber-500 hover:bg-amber-600 text-white font-medium" data-testid="sub-submit-btn">
                <Check className="w-4 h-4 mr-1.5" /> Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scan ID Card Dialog */}
        <Dialog open={scanDialogOpen} onOpenChange={(open) => { setScanDialogOpen(open); if (!open) { setCheckinResult(null); setScanStep("select"); setSelectedMeeting(null); } }}>
          <DialogContent className="bg-card border-[var(--border-color)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-primary text-center text-lg flex items-center justify-center gap-2">
                <Camera className="w-5 h-5 text-bch-teal" weight="fill" />
                Scan Member ID Card
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {checkinResult ? (
                <div className="flex flex-col items-center gap-4" data-testid="checkin-result">
                  <CheckCircle
                    className={`w-16 h-16 ${checkinResult.success ? 'text-bch-teal' : 'text-red-400'}`}
                    weight="fill"
                  />
                  <p className={`text-lg font-medium text-center ${checkinResult.success ? 'text-bch-teal' : 'text-red-400'}`}>
                    {checkinResult.message}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => { setCheckinResult(null); }}
                      className="bg-bch-teal hover:bg-bch-teal-dark text-white"
                      data-testid="scan-another-btn"
                    >
                      <Camera className="w-4 h-4 mr-1.5" /> Scan Another
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setScanDialogOpen(false); setCheckinResult(null); setScanStep("select"); setSelectedMeeting(null); }}
                      className="text-secondary"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : scanStep === "select" ? (
                <div className="space-y-3" data-testid="select-meeting-step">
                  <p className="text-secondary text-sm text-center mb-4">Select a meeting to mark attendance</p>
                  {meetings.filter(m => m.status === "upcoming").length === 0 ? (
                    <p className="text-muted text-sm text-center py-6">No upcoming meetings</p>
                  ) : (
                    meetings.filter(m => m.status === "upcoming").map((m) => (
                      <button
                        key={m.id}
                        onClick={() => { setSelectedMeeting(m); setScanStep("scan"); }}
                        className="w-full text-left bg-surface border border-[var(--border-color)] rounded-xl p-4 hover:border-bch-teal/50 hover:bg-bch-teal/5 transition-all"
                        data-testid={`select-meeting-${m.id}`}
                      >
                        <h4 className="text-primary font-semibold text-sm">{m.title}</h4>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-muted text-xs flex items-center gap-1"><CalendarCheck className="w-3.5 h-3.5" />{m.meeting_date}</span>
                          <span className="text-muted text-xs flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{m.meeting_time}</span>
                        </div>
                        <p className="text-bch-gold text-xs mt-1">{m.chapter_name}</p>
                      </button>
                    ))
                  )}
                </div>
              ) : (
                <div data-testid="scan-step">
                  <div className="bg-surface border border-[var(--border-color)] rounded-xl p-3 mb-4">
                    <p className="text-primary font-semibold text-sm">{selectedMeeting?.title}</p>
                    <p className="text-muted text-xs mt-0.5">{selectedMeeting?.meeting_date} &middot; {selectedMeeting?.meeting_time}</p>
                  </div>
                  <p className="text-secondary text-sm text-center mb-3">Scan member's BCH ID card QR code</p>
                  <QRScanner
                    onScan={handleQRScan}
                    onClose={() => setScanDialogOpen(false)}
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Visitor Dialog */}
        <Dialog open={visitorDialogOpen} onOpenChange={setVisitorDialogOpen}>
          <DialogContent className="bg-card border-[var(--border-color)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-primary text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-violet-500" weight="fill" />
                Add Visitor to Meeting
              </DialogTitle>
            </DialogHeader>
            {visitorMeeting && (
              <div className="space-y-2 -mt-1">
                <p className="text-primary font-semibold text-sm">{visitorMeeting.title} &middot; {visitorMeeting.meeting_date}</p>
                {visitorMeeting.location && (() => {
                  const parts = visitorMeeting.location.split(" | ");
                  const venue = parts[0];
                  const address = parts[1] && !parts[1].startsWith("http") ? parts[1] : null;
                  const mapsLink = parts.find(p => p.startsWith("http"));
                  return (
                    <div className="bg-surface border border-[var(--border-color)] rounded-xl p-3 space-y-1.5">
                      <div className="flex items-center gap-2 text-secondary text-xs">
                        <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" weight="fill" />
                        <span className="font-medium">{venue}</span>
                      </div>
                      {address && <p className="text-muted text-xs ml-[22px]">{address}</p>}
                      {mapsLink && (
                        <a href={mapsLink} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 ml-[22px] text-blue-500 text-[11px] font-medium hover:underline"
                          data-testid="visitor-maps-link"
                        >
                          <MapPin className="w-3 h-3" weight="fill" /> Open in Google Maps
                        </a>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-secondary text-xs font-medium">Visitor Name *</Label>
                <Input
                  value={visitorForm.name}
                  onChange={(e) => setVisitorForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-surface border-[var(--border-color)] text-primary"
                  placeholder="Full name"
                  data-testid="visitor-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-secondary text-xs font-medium">Contact Number *</Label>
                <Input
                  value={visitorForm.contact}
                  onChange={(e) => setVisitorForm(p => ({ ...p, contact: e.target.value }))}
                  className="bg-surface border-[var(--border-color)] text-primary"
                  placeholder="+91 9876543210"
                  data-testid="visitor-contact"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-secondary text-xs font-medium">Business Name *</Label>
                <Input
                  value={visitorForm.business_name}
                  onChange={(e) => setVisitorForm(p => ({ ...p, business_name: e.target.value }))}
                  className="bg-surface border-[var(--border-color)] text-primary"
                  placeholder="Company / Business name"
                  data-testid="visitor-business"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-secondary text-xs font-medium">Business Category</Label>
                <Input
                  value={visitorForm.category}
                  onChange={(e) => setVisitorForm(p => ({ ...p, category: e.target.value }))}
                  className="bg-surface border-[var(--border-color)] text-primary"
                  placeholder="e.g. IT Services, Real Estate"
                  data-testid="visitor-category"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-secondary text-xs font-medium">Invited By (Member)</Label>
                <Select value={visitorForm.invited_by_id} onValueChange={(v) => setVisitorForm(p => ({ ...p, invited_by_id: v }))}>
                  <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary" data-testid="visitor-invited-by">
                    <SelectValue placeholder="Select member who invited" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-[var(--border-color)]">
                    {chapterMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-primary">{m.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <Label className="text-secondary text-xs font-medium">Interested to Join BCH?</Label>
                <button
                  type="button"
                  onClick={() => setVisitorForm(p => ({ ...p, interested_to_join: !p.interested_to_join }))}
                  data-testid="visitor-interested"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${visitorForm.interested_to_join ? "bg-bch-teal" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${visitorForm.interested_to_join ? "translate-x-6" : "translate-x-1"}`} />
                </button>
                <span className={`text-xs font-medium ${visitorForm.interested_to_join ? "text-bch-teal" : "text-muted"}`}>
                  {visitorForm.interested_to_join ? "Yes" : "No"}
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setVisitorDialogOpen(false)} className="text-secondary">Cancel</Button>
              <Button onClick={handleAddVisitor} className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="submit-visitor">
                Add Visitor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Visitors Dialog */}
        <Dialog open={viewVisitorsOpen} onOpenChange={setViewVisitorsOpen}>
          <DialogContent className="bg-card border-[var(--border-color)] max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-primary text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-500" weight="fill" />
                Meeting Visitors
              </DialogTitle>
            </DialogHeader>
            {visitorMeeting && (
              <p className="text-muted text-xs -mt-2">{visitorMeeting.title} &middot; {visitorMeeting.meeting_date}</p>
            )}
            <div className="max-h-80 overflow-y-auto space-y-3 py-2">
              {visitors.length > 0 ? visitors.map((v) => (
                <div key={v.id} className="bg-surface border border-[var(--border-color)] rounded-xl p-4" data-testid={`visitor-${v.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-primary font-semibold text-sm">{v.name}</h4>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                        <span className="text-muted text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {v.contact}
                        </span>
                        <span className="text-muted text-xs flex items-center gap-1">
                          <Briefcase className="w-3 h-3" /> {v.business_name}
                        </span>
                        {v.category && (
                          <span className="text-muted text-xs flex items-center gap-1">
                            <Tag className="w-3 h-3" /> {v.category}
                          </span>
                        )}
                      </div>
                      {v.invited_by_name && (
                        <p className="text-xs text-pink-500 mt-1.5 flex items-center gap-1">
                          <UserPlus className="w-3 h-3" /> Invited by <span className="font-medium">{v.invited_by_name}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {/* Status badge */}
                      <span className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg font-medium ${
                        v.status === "present" 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {v.status === "present" ? (
                          <><CheckCircle className="w-3 h-3" weight="fill" /> Present</>
                        ) : (
                          <><Clock className="w-3 h-3" weight="fill" /> Registered</>
                        )}
                      </span>
                      {/* Mark present button for leadership */}
                      {canAddVisitors && visitorMeeting && (
                        <button
                          onClick={() => handleMarkPresent(visitorMeeting.id, v.id)}
                          data-testid={`mark-present-${v.id}`}
                          className={`text-[10px] font-medium px-2 py-1 rounded-lg transition-colors ${
                            v.status === "present"
                              ? "bg-red-100 text-red-600 hover:bg-red-200"
                              : "bg-bch-teal/15 text-bch-teal hover:bg-bch-teal/25"
                          }`}
                        >
                          {v.status === "present" ? "Undo Present" : "Mark Present"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <Users className="w-10 h-10 text-muted/20 mx-auto mb-2" weight="duotone" />
                  <p className="text-muted text-sm">No visitors recorded for this meeting</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <InviteLinkModal
        isOpen={inviteModal.open}
        onClose={() => setInviteModal({ open: false, category: "", chapterName: "", chapterId: "", meetingId: "" })}
        category={inviteModal.category}
        chapterName={inviteModal.chapterName}
        chapterId={inviteModal.chapterId}
        meetingId={inviteModal.meetingId}
        inviterName={user?.full_name}
        inviterId={user?.id}
      />
    </>
  );
}
